import { createAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import type { ProductType } from '@/api/user/getCurrentUser.ts';
import type {
  BlotterLegOptionProductType,
  BlotterSingleOrCompoundOptionProductType,
} from '@/store/blotter/api/types/BlotterTradeEntry.ts';
import type { BlotterCashEntry } from '@/store/blotter/types/BlotterCashEntry.ts';
import type { BlotterOptionEntry } from '@/store/blotter/types/BlotterOptionEntry.ts';
import type { OptionUserPreference } from '@/store/option/state.ts';

/**
 * The cashRepriceQuote action creates a new tile from a blotter entry as model
 * It is handled by multiple slices for state modification and effects
 */
export const cashRepriceQuote = createAction('blotter/cashRepriceQuote', (entry: BlotterCashEntry, currentTabId) => ({
  payload: {
    tileId: uuidv4(),
    sessionId: uuidv4(),
    currentTabId,
    entry,
    productType:
      entry.product === 'Spot' || entry.product === 'Outright' || entry.product === 'Ndf'
        ? 'Cash'
        : ('Swap' as ProductType),
  },
}));

type RepriceOptionPayload = {
  productType: ProductType;
  tileId: string;
  currentTabId: string;
  entry: BlotterOptionEntry;
  children: BlotterOptionEntry[];
  userPreferences: OptionUserPreference;
};
export const optionRepriceQuote = createAction(
  'blotter/optionRepriceQuote',
  (
    entry: BlotterOptionEntry,
    children: BlotterOptionEntry[],
    currentTabId: string,
    userPreferences: OptionUserPreference,
  ): { payload: RepriceOptionPayload } => {
    return {
      payload: {
        tileId: uuidv4(),
        currentTabId,
        entry,
        productType: getOptionProduct(entry.product),
        children,
        userPreferences,
      },
    };
  },
);

function getOptionProduct(
  product: BlotterSingleOrCompoundOptionProductType | BlotterLegOptionProductType,
): ProductType {
  switch (product) {
    case 'Vanilla':
    case 'Strategy':
    case 'Straddle':
    case 'Strangle':
    case 'RiskReversal':
      return 'Option';
    case 'AmericanForward':
      return 'FlexiForward';
    default:
      throw new Error(`${product} is not an option`);
  }
}
